/**
 * useLgpdRequests — hook LGPD Requests (Brief 5 B1 Centro Direitos do Titular).
 *
 * Sprint Frontend Desktop W3.2. Consome endpoints W2.2 lgpd/ módulo (PR #65).
 *
 * Capabilities server-side: lgpd-requests-read (Team+/DPO/admin_tenant).
 * Decisão executiva: staleTime 60s — dados LGPD não mudam com alta frequência;
 * mutation invalida cache canonicamente via queryKey['lgpd-requests'].
 *
 * ADRs: ADR-024 + ADR-098 + ADR-110.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  LgpdRequestStatus,
  LgpdRequestType,
  LgpdRequestWireItem,
} from '../features/dpo/centro-direitos/types';

const QUERY_KEY = 'lgpd-requests';
const STALE_TIME = 60_000;

// ─── List ─────────────────────────────────────────────────────────────────────

export interface UseLgpdRequestsFilters {
  status?: LgpdRequestStatus | undefined;
  requestType?: LgpdRequestType | undefined;
}

export function useLgpdRequests(filters: UseLgpdRequestsFilters = {}): {
  data: LgpdRequestWireItem[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  isPlaceholder: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: [QUERY_KEY, filters.status ?? 'all', filters.requestType ?? 'all'],
    queryFn: async () => {
      return api.listLgpdRequests({
        ...(filters.status !== undefined ? { status: filters.status } : {}),
        ...(filters.requestType !== undefined ? { requestType: filters.requestType } : {}),
        limit: 50,
      });
    },
    staleTime: STALE_TIME,
    retry: 1,
  });

  // Defensive placeholder when data is not yet available
  const isPlaceholder = query.data === undefined && !query.isLoading && !query.isError;

  const items = query.data?.items ?? [];
  return {
    data: items,
    // O Engine é cursor-paginado e não devolve total; usamos a contagem da página.
    total: items.length,
    isLoading: query.isLoading,
    isError: query.isError,
    isPlaceholder,
    refetch: () => void query.refetch(),
  };
}

// ─── Single request ───────────────────────────────────────────────────────────

export function useLgpdRequest(requestId: string | null): {
  data: LgpdRequestWireItem | null;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [QUERY_KEY, 'detail', requestId],
    queryFn: async () => {
      if (requestId === null) throw new Error('requestId required');
      return api.getLgpdRequest(requestId);
    },
    enabled: requestId !== null,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// ─── Create ───────────────────────────────────────────────────────────────────

export function useCreateLgpdRequest(): ReturnType<
  typeof useMutation<LgpdRequestWireItem, Error, Parameters<typeof api.createLgpdRequest>[0]>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createLgpdRequest>[0]) => api.createLgpdRequest(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

// ─── Transition (approve/reject/etc.) ─────────────────────────────────────────

export function useTransitionLgpdRequest(): ReturnType<
  typeof useMutation<
    LgpdRequestWireItem,
    Error,
    {
      requestId: string;
      version: number;
      newStatus: string;
      rejectionReason?: string;
      responseLetter?: string;
      reviewedAt?: string;
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      version,
      newStatus,
      rejectionReason,
      responseLetter,
      reviewedAt,
    }: {
      requestId: string;
      version: number;
      newStatus: string;
      rejectionReason?: string;
      responseLetter?: string;
      reviewedAt?: string;
    }) =>
      api.transitionLgpdRequest(requestId, version, {
        newStatus,
        ...(rejectionReason !== undefined ? { rejectionReason } : {}),
        ...(responseLetter !== undefined ? { responseLetter } : {}),
        ...(reviewedAt !== undefined ? { reviewedAt } : {}),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      void qc.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', vars.requestId] });
    },
  });
}

// ─── Assign DPO ───────────────────────────────────────────────────────────────

export function useAssignLgpdDpo(): ReturnType<
  typeof useMutation<
    LgpdRequestWireItem,
    Error,
    { requestId: string; version: number; dpoUserId: string }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      version,
      dpoUserId,
    }: {
      requestId: string;
      version: number;
      dpoUserId: string;
    }) => api.assignLgpdDpo(requestId, version, dpoUserId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      void qc.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', vars.requestId] });
    },
  });
}
